import type { Meta, StoryFn } from '@storybook/react';

import {
  ThemeOverride,
  RadioButton,
  RadioButtonCard as RadioButtonCardComp,
  Link
} from '@pega/cosmos-react-core';
import type {
  ImagePlacement,
  ImageSize,
  RadioButtonCardProps,
  RadioCheckProps
} from '@pega/cosmos-react-core';

export default {
  title: 'Core/RadioButton',
  component: RadioButton,
  excludeStories: ['ConfigurableRadioButton'],
  args: {
    status: undefined,
    label: 'Choose me',
    required: false,
    disabled: false,
    readOnly: false,
    showAdditionalInfo: false
  },
  argTypes: {
    status: { options: [undefined, 'success', 'warning', 'error'], control: { type: 'select' } },
    label: { control: { type: 'text' } },
    required: { control: { type: 'boolean' } },
    disabled: { control: { type: 'boolean' } },
    readOnly: { control: { type: 'boolean' } },
    showAdditionalInfo: { control: { type: 'boolean' } }
  }
} as Meta;

interface RadioCheckStoryProps extends RadioCheckProps {
  showAdditionalInfo?: boolean;
}

export const DefaultRadioButton: StoryFn<RadioCheckStoryProps> = (args: RadioCheckStoryProps) => {
  return (
    <RadioButton
      additionalInfo={
        args.showAdditionalInfo
          ? {
              content: 'Some additional info'
            }
          : undefined
      }
      status={args.status}
      info={args.info}
      label={args.label}
      required={args.required}
      disabled={args.disabled}
      readOnly={args.readOnly}
    />
  );
};

DefaultRadioButton.args = {
  info: 'You must select it'
};

DefaultRadioButton.argTypes = {
  info: { control: { type: 'text', label: 'Helper text' } }
};

interface RadioButtonCardStoryProps extends RadioButtonCardProps {
  imagePlacement?: ImagePlacement;
  imageSize?: ImageSize;
  imageUrl: string;
  noOfFields: number;
  showAdditionalInfo: boolean;
  showActions: boolean;
  removeFieldLabels: boolean;
}

export const RadioButtonCard: StoryFn<RadioButtonCardStoryProps> = (
  args: RadioButtonCardStoryProps
) => {
  const imgAltText = 'The image alternative text';

  return (
    <RadioButtonCardComp
      required={args.required}
      label={args.label}
      disabled={args.disabled}
      readOnly={args.readOnly}
      indeterminate={args.indeterminate}
      additionalInfo={args.showAdditionalInfo ? { content: 'Some additional info' } : undefined}
      actions={
        args.showActions
          ? [
              {
                id: 'id',
                text: 'First action',
                onClick: () => {}
              }
            ]
          : undefined
      }
      image={
        args.imageUrl
          ? {
              src: args.imageUrl,
              size: args.imageSize,
              alt: imgAltText,
              placement: args.imagePlacement
            }
          : undefined
      }
      fields={
        args.noOfFields > 0
          ? [
              { id: 'service', name: 'Service length', value: '1 year' },
              {
                id: 'monthly-cost',
                name: 'Monthly cost',
                value: <Link href='https://www.pega.com/'>https://www.pega.com/</Link>
              },
              { id: 'total-cost', name: 'Total cost', value: '$2,160.00' },
              {
                id: 'text',
                name: 'Text',
                value:
                  'Lorem ipsum is a dummy or placeholder text commonly used in graphic design, publishing, and web development to fill empty spaces in a layout that does not yet have content.'
              },
              {
                id: 'phone',
                name: 'Phone',
                value: '1-800-123-456'
              }
            ]
              .slice(0, args.noOfFields)
              .map(field => (args.removeFieldLabels ? { id: field.id, value: field.value } : field))
          : undefined
      }
    />
  );
};

RadioButtonCard.args = {
  imagePlacement: 'inline-start',
  imageSize: 'flexible',
  imageUrl:
    'https://www.pega.com/sites/default/files/styles/640/public/media/images/2024-01/pega-hp-streamline-ops-card-img-v2.jpg?itok=WO_wAay2',
  noOfFields: 5,
  showActions: false,
  status: undefined,
  label: 'Radio button card label',
  removeFieldLabels: false
};

RadioButtonCard.argTypes = {
  imagePlacement: {
    options: ['inline-start', 'inline-end', 'block-start'],
    control: { type: 'select' }
  },
  imageSize: { options: ['large', 'small', 'flexible'], control: { type: 'select' } },
  noOfFields: { control: { type: 'number' } },
  imageUrl: { control: { type: 'text' } },
  status: { table: { disable: true } },
  label: { control: { type: 'text' } },
  showActions: { control: { type: 'boolean' } },
  removeFieldLabels: { control: { type: 'boolean' } }
};

interface ConfigurableRadioButtonProps extends Omit<RadioCheckProps, 'size'> {
  size?: string;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: string;
  borderRadius?: string;
  labelColor?: string;
  labelFontWeight?: string;
  checkedBackgroundColor?: string;
  checkedBorderColor?: string;
  showAdditionalInfo?: boolean;
}

export const ConfigurableRadioButton: StoryFn<ConfigurableRadioButtonProps> = (
  args: ConfigurableRadioButtonProps
) => {
  return (
    <ThemeOverride
      theme={{
        components: {
          'radio-button': {
            'border-radius': args.borderRadius
          },
          'radio-check': {
            'border-color': args.borderColor,
            'border-width': args.borderWidth,
            'background-color': args.backgroundColor,
            label: {
              color: args.labelColor,
              'font-weight': args.labelFontWeight
            },
            size: args.size,
            ':checked': {
              'background-color': args.checkedBackgroundColor,
              'border-color': args.checkedBorderColor
            }
          }
        }
      }}
    >
      <RadioButton
        additionalInfo={
          args.showAdditionalInfo
            ? {
                content: 'Some additional info'
              }
            : undefined
        }
        label='Configurable Radio Button'
      />
    </ThemeOverride>
  );
};

ConfigurableRadioButton.args = {
  size: '1.25rem',
  backgroundColor: '#ffffff',
  borderColor: '#939393',
  borderWidth: '0.0625rem',
  borderRadius: '50%',
  labelColor: '#939393',
  labelFontWeight: '400',
  checkedBackgroundColor: '#076bc9',
  checkedBorderColor: '#076bc9'
};

ConfigurableRadioButton.argTypes = {
  size: { control: { type: 'text' } },
  backgroundColor: { control: { type: 'color' } },
  borderColor: { control: { type: 'color' } },
  borderWidth: { control: { type: 'text' } },
  borderRadius: { control: { type: 'text' } },
  labelColor: { control: { type: 'color' } },
  labelFontWeight: { control: { type: 'text' } },
  checkedBackgroundColor: { control: { type: 'color' } },
  checkedBorderColor: { control: { type: 'color' } },
  status: { table: { disable: true } },
  label: { table: { disable: true } },
  required: { table: { disable: true } },
  disabled: { table: { disable: true } },
  readOnly: { table: { disable: true } }
};
